import { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Button, Loader } from '../../components/common';

import { Flex, Text, Effects } from '../../components/common';
import { requestOTP, verifyOTP } from '../../api/auth';
import SignupModal from './signup.modal';
import styles from './styles.module.scss';

const initialState = {
  username: '',
  errorMessage: null,
  fetching: false,
  showSignupModal: false,
  otpStep: 'email', // 'email' or 'code'
  otpCode: '',
};
export default function Login({ syncUserData, signup }) {
  const [form, setForm] = useState(initialState);
  const updateState = (changes) => setForm({ ...form, ...changes });
  const { width } = Effects.useDimensions();
  const otpInputRefs = useRef(
    Array(6)
      .fill(0)
      .map(() => ({ current: null })),
  );

  // OTP handlers
  const handleRequestOTP = async (e) => {
    e?.preventDefault();
    updateState({ errorMessage: null, fetching: true });

    try {
      await requestOTP(form.username.toLowerCase().trim());
      updateState({ otpStep: 'code', otpCode: '', fetching: false });
    } catch (error) {
      updateState({ errorMessage: error.message, fetching: false });
    }
  };

  const handleVerifyOTP = async (e) => {
    e?.preventDefault();
    updateState({ errorMessage: null, fetching: true });

    try {
      await verifyOTP(form.username.toLowerCase().trim(), form.otpCode);
      // Fetch user data and update Redux store
      await syncUserData();
    } catch (error) {
      updateState({
        errorMessage: error.message,
        fetching: false,
        otpCode: '',
      });
    }
  };

  const handleOTPInputChange = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const newCode = form.otpCode.split('');
    newCode[index] = digit;
    const updatedCode = newCode.join('');

    updateState({ otpCode: updatedCode });

    // Auto-focus next input
    if (digit && index < 5) {
      otpInputRefs.current[index + 1].current?.focus();
    }
  };

  const handleOTPInputKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !form.otpCode[index] && index > 0) {
      otpInputRefs.current[index - 1].current?.focus();
    }
  };

  const handleOTPPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const digits = pastedData.replace(/\D/g, '').slice(0, 6);

    if (digits.length > 0) {
      updateState({ otpCode: digits });

      const nextEmptyIndex = Math.min(digits.length, 5);
      otpInputRefs.current[nextEmptyIndex].current?.focus();
    }
  };

  let leftColumn = null;
  if (width > 900) {
    leftColumn = <div className={styles.leftColumn} />;
  }

  const body = (
    <div className={styles.loginForm}>
      <div alt="logo" className={styles.logoImage} />

      {form.fetching && <Loader />}
      {form.otpStep === 'email' && !form.fetching && (
        <div className="flex-col">
          <div className={styles.loginInfo}>
            Welcome to your online Alice account.
            <br />
            Enter your email to receive a verification code
          </div>
          <form className="flex-col" onSubmit={handleRequestOTP}>
            <div className={styles.inputGroup}>
              <div className={styles.inputIcon}>
                <i className="fas fa-envelope"></i>
              </div>
              <input
                className={styles.modernInput}
                type="email"
                name="username"
                id="username"
                placeholder="Email Address"
                value={form.username}
                onChange={(e) => updateState({ username: e.target.value })}
                autoFocus
                required
              />
            </div>

            {form.errorMessage && (
              <p className="text-orange-600 text-sm my-2 self-center text-center">
                {form.errorMessage}
              </p>
            )}

            <Button
              mt={10}
              submit
              stretch
              disabled={!form.username || !form.username.includes('@')}
              type="submit"
            >
              Send Verification Code
            </Button>

            <Button
              mt={10}
              primary
              stretch
              onClick={() => updateState({ showSignupModal: true })}
              type="button"
            >
              Create Account
            </Button>
          </form>
        </div>
      )}

      {/* Code Entry Step */}
      {form.otpStep === 'code' && !form.fetching && (
        <>
          <div className={styles.loginInfo}>
            Enter the 6-digit code sent to:
            <br />
            <strong>{form.username}</strong>
          </div>
          <form onSubmit={handleVerifyOTP}>
            <div className={styles.otpContainer}>
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <input
                  key={index}
                  ref={otpInputRefs.current[index]}
                  className={styles.otpBox}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={form.otpCode[index] || ''}
                  onChange={(e) => handleOTPInputChange(index, e.target.value)}
                  onKeyDown={(e) => handleOTPInputKeyDown(index, e)}
                  onPaste={handleOTPPaste}
                  autoFocus={index === 0}
                />
              ))}
            </div>

            {form.errorMessage && (
              <p className="text-orange-600 text-sm mb-4 self-center text-center">
                {form.errorMessage}
              </p>
            )}

            <Button
              mt={10}
              submit
              stretch
              disabled={form.otpCode.length !== 6}
              type="submit"
            >
              Verify Code
            </Button>

            <Button
              mt={10}
              secondary
              stretch
              onClick={() =>
                updateState({
                  otpStep: 'email',
                  otpCode: '',
                  errorMessage: null,
                })
              }
              type="button"
            >
              Back to Login
            </Button>
          </form>
        </>
      )}
    </div>
  );

  return (
    <Flex
      className={classNames(styles.container, {
        [styles.loading]: form.fetching,
      })}
      growToContainer
      justifyContent="center"
      alignItems="stretch"
    >
      {leftColumn}
      <Flex
        style={{ flex: 1, minWidth: '40%' }}
        alignItems="center"
        justifyContent="center"
      >
        {body}
      </Flex>
      <SignupModal
        isVisible={form.showSignupModal}
        onClose={() => updateState({ showSignupModal: false })}
        signup={signup}
      />
    </Flex>
  );
}

Login.propTypes = {
  syncUserData: PropTypes.func.isRequired,
};
